// src/pages/orders/components/OrderStatusSection.jsx
import React, { useState } from 'react';
import {
  CheckCircle,
  CreditCard,
  Calendar,
  AlertTriangle,
  RefreshCw,
  ChevronDown,
  Search,
  Loader2,
  CheckCircle2,
  XCircle,
  Edit2,
  Truck,
  RotateCcw
} from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatCurrency } from '../../../utils/currencyUtil';
import Dropdown from '../../../components/ui/Dropdown/Dropdown';
import styles from './OrderStatusSection.module.css';

const VERIFIED_SWITCHABLE_PAYMENT_TYPES = ['fully_paid', 'partially_paid', 'store_pickup'];

const OrderStatusSection = ({
  formData,
  referenceData,
  selectableOrderStatuses = [],
  errors,
  onFieldChange,
  getStatusReasons,
  isReplacementOrder,
  replacedOrderData,
  isLookingUpOrder,
  orderLookupError,
  isEditMode = false,
  orderTotals,
  onUpdateReturnItemQty,
  returnPackageOrder = null
}) => {
  const { t } = useLocalization();
  const [isLookupUnlocked, setIsLookupUnlocked] = useState(false);
  const isReturnLocked = Boolean(returnPackageOrder);

  const selectedStatus = referenceData.orderStatuses.find(s => s.key === formData.orderStatusKey);
  const selectedPaymentType = referenceData.paymentTypes.find(p => p.key === formData.paymentTypeKey);
  const statusReasons = getStatusReasons();
  const hasVerifiedPayment = formData.paymentRecords?.some(r => r.isVerified);
  const { hasPermission } = usePermissions();
  const canOverrideVerifiedPaymentType = hasPermission('edit_verified_order_payment_type');
  const isPaymentTypeLocked = hasVerifiedPayment && !canOverrideVerifiedPaymentType;
  const paymentTypeOptions = hasVerifiedPayment
    ? referenceData.paymentTypes.filter(type => VERIFIED_SWITCHABLE_PAYMENT_TYPES.includes(type.key) || type.key === formData.paymentTypeKey)
    : referenceData.paymentTypes;

  // Handler for COD amount quick fill
  const handleCodQuickFill = (value) => {
    onFieldChange('replacementCodAmount', value);
  };

  // Check if lookup field should be read-only
  const isLookupReadOnly = isEditMode && formData.replacedOrderId && replacedOrderData && !isLookupUnlocked;

  return (
    <div className={styles.orderStatusContent}>
      <div className={styles.formGrid}>
        {/* Order Status */}
        <div className={styles.formGroup}>
          <label htmlFor="orderStatus">
            {t('ecomOrders.orderStatus')} *
          </label>
          <Dropdown
            value={formData.orderStatusKey}
            onChange={(value) => value && onFieldChange('orderStatusKey', value)}
            options={selectableOrderStatuses.map((status) => ({ value: status.key, label: status.label }))}
            icon={<CheckCircle size={18} />}
            allowClear={false}
          />
          {errors.orderStatusKey && (
            <span className={styles.errorText}>{errors.orderStatusKey}</span>
          )}
        </div>

        {/* Payment Type */}
        <div className={styles.formGroup}>
          <label htmlFor="paymentType">
            {t('ecomOrders.paymentType')} *
          </label>
          <div title={isPaymentTypeLocked ? t('ecomOrders.paymentTypeLockedHint') : undefined}>
            <Dropdown
              value={formData.paymentTypeKey}
              onChange={(value) => value && onFieldChange('paymentTypeKey', value)}
              options={paymentTypeOptions.map((type) => ({ value: type.key, label: type.label }))}
              icon={<CreditCard size={18} />}
              allowClear={false}
              disabled={isPaymentTypeLocked}
              className={errors.paymentTypeKey ? styles.error : ''}
            />
          </div>
          {errors.paymentTypeKey && (
            <span className={styles.errorText}>{errors.paymentTypeKey}</span>
          )}
        </div>

        {/* Status Reason (conditional) */}
        {selectedStatus?.requiresReason && (
          <div className={styles.formGroup}>
            <label htmlFor="statusReason">
              {t('ecomOrders.statusReason')} *
            </label>
            <div className={styles.selectWrapper}>
              <AlertTriangle className={styles.inputIcon} size={18} />
              <select
                id="statusReason"
                value={formData.orderStatusReasonKey || ''}
                onChange={(e) => onFieldChange('orderStatusReasonKey', e.target.value)}
                className={errors.orderStatusReasonKey ? styles.error : ''}
                required
              >
                <option value="">{t('ecomOrders.selectStatusReason')}</option>
                {statusReasons.map(reason => (
                  <option key={reason.key} value={reason.key}>
                    {reason.label}
                  </option>
                ))}
              </select>
              <ChevronDown className={styles.selectArrow} size={18} />
            </div>
            {errors.orderStatusReasonKey && (
              <span className={styles.errorText}>{errors.orderStatusReasonKey}</span>
            )}
          </div>
        )}

        {/* Hold Until Date (conditional) */}
        {selectedStatus?.requiresHoldDate && (
          <div className={styles.formGroup}>
            <label htmlFor="holdUntilDate">
              {t('ecomOrders.holdUntilDate')} *
            </label>
            <div className={styles.inputWrapper}>
              <Calendar className={styles.inputIcon} size={18} />
              <input
                type="date"
                id="holdUntilDate"
                value={formData.holdUntilDate || ''}
                onChange={(e) => onFieldChange('holdUntilDate', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className={errors.holdUntilDate ? styles.error : ''}
                required
              />
            </div>
            {errors.holdUntilDate && (
              <span className={styles.errorText}>{errors.holdUntilDate}</span>
            )}
          </div>
        )}
      </div>

      {/* Replacement Order Fields */}
      {isReplacementOrder && (
        <div className={styles.replacementSection}>
          <div className={styles.sectionHeader}>
            <RefreshCw className={styles.sectionIcon} size={18} />
            <h3>{t('ecomOrders.replacementOrders')}</h3>
          </div>
          
          <div className={styles.formGrid}>
            {/* Order Lookup Input */}
            <div className={styles.formGroupFull}>
              <div className={styles.labelWithAction}>
                <label htmlFor="replacedOrderLookup">
                  {t('ecomOrders.replacedOrderLookup')} *
                </label>
                {isLookupReadOnly && (
                  <button
                    type="button"
                    onClick={() => setIsLookupUnlocked(true)}
                    className={styles.unlockButton}
                  >
                    <Edit2 size={14} />
                    Change Order
                  </button>
                )}
              </div>
              <div className={`${styles.inputWrapper} ${styles.lookupWrapper}`}>
                <Search className={styles.inputIcon} size={18} />
                <input
                  type="text"
                  id="replacedOrderLookup"
                  value={formData.replacedOrderLookupValue || ''}
                  onChange={(e) => onFieldChange('replacedOrderLookupValue', e.target.value)}
                  placeholder={t('ecomOrders.replacedOrderLookupPlaceholder')}
                  className={errors.replacedOrderLookupValue ? styles.error : ''}
                  required
                  readOnly={isLookupReadOnly}
                />
                {!isEditMode && isLookingUpOrder && (
                  <Loader2 className={styles.lookupSpinner} size={18} />
                )}
                {!isEditMode && !isLookingUpOrder && formData.replacedOrderLookupValue && !orderLookupError && replacedOrderData && (
                  <CheckCircle2 className={styles.lookupSuccess} size={18} />
                )}
                {!isEditMode && !isLookingUpOrder && formData.replacedOrderLookupValue && orderLookupError && (
                  <XCircle className={styles.lookupError} size={18} />
                )}
              </div>
              {errors.replacedOrderLookupValue && (
                <span className={styles.errorText}>{errors.replacedOrderLookupValue}</span>
              )}
              
              {/* Order Lookup Result */}
              {replacedOrderData && !isLookupReadOnly && (
                <div className={styles.lookupResult}>
                  <div className={styles.lookupResultGrid}>
                    <div className={styles.lookupResultItem}>
                      <span className={styles.lookupLabel}>Order #:</span>
                      <span className={styles.lookupValue}>{replacedOrderData.orderNumber}</span>
                    </div>
                    {replacedOrderData.airwaybillNumber && (
                      <div className={styles.lookupResultItem}>
                        <span className={styles.lookupLabel}>AWB:</span>
                        <span className={styles.lookupValue}>{replacedOrderData.airwaybillNumber}</span>
                      </div>
                    )}
                    <div className={styles.lookupResultItem}>
                      <span className={styles.lookupLabel}>Total:</span>
                      <span className={styles.lookupValue}>{formatCurrency(replacedOrderData.grandTotal)}</span>
                    </div>
                    <div className={styles.lookupResultItem}>
                      <span className={styles.lookupLabel}>Status:</span>
                      <span 
                        className={styles.lookupStatus}
                        style={{ 
                          backgroundColor: `${replacedOrderData.OrderStatus.colorCode}15`,
                          color: replacedOrderData.OrderStatus.colorCode
                        }}
                      >
                        {replacedOrderData.OrderStatus.label}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Replacement Reason */}
            <div className={styles.formGroup}>
              <label htmlFor="replacementReason">
                {t('ecomOrders.replacementReason')} *
              </label>
              <div className={styles.selectWrapper}>
                <AlertTriangle className={styles.inputIcon} size={18} />
                <select
                  id="replacementReason"
                  value={formData.replacementReasonKey || ''}
                  onChange={(e) => onFieldChange('replacementReasonKey', e.target.value)}
                  className={errors.replacementReasonKey ? styles.error : ''}
                  required
                >
                  <option value="">{t('ecomOrders.selectReplacementReason')}</option>
                  {referenceData.replacementReasons.map(reason => (
                    <option key={reason.key} value={reason.key}>
                      {reason.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className={styles.selectArrow} size={18} />
              </div>
              {errors.replacementReasonKey && (
                <span className={styles.errorText}>{errors.replacementReasonKey}</span>
              )}
            </div>

            {/* COD Amount Field */}
            <div className={styles.formGroup}>
              <label htmlFor="replacementCodAmount">
                {t('ecomOrders.codAmount')} *
              </label>
              <div className={styles.inputWrapper}>
                <CreditCard className={styles.inputIcon} size={18} />
                <input
                  type="number"
                  id="replacementCodAmount"
                  value={formData.replacementCodAmount}
                  onChange={(e) => onFieldChange('replacementCodAmount', e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                  className={errors.replacementCodAmount ? styles.error : ''}
                  required
                />
              </div>
              {errors.replacementCodAmount && (
                <span className={styles.errorText}>{errors.replacementCodAmount}</span>
              )}
              <div className={styles.codQuickFill}>
                <button
                  type="button"
                  onClick={() => handleCodQuickFill(0)}
                  className={styles.quickFillLink}
                >
                  0 COD
                </button>
                <span className={styles.quickFillSeparator}>•</span>
                <button
                  type="button"
                  onClick={() => handleCodQuickFill(orderTotals?.deliveryCharge || 0)}
                  className={styles.quickFillLink}
                >
                  Delivery Charge Only
                </button>
              </div>
            </div>

            {/* Return Airwaybill Number */}
            <div className={styles.formGroup}>
              <label htmlFor="returnAirwaybillNumber">
                {t('ecomOrders.returnAirwaybillNumber')}
              </label>
              <div className={styles.inputWrapper}>
                <Truck className={styles.inputIcon} size={18} />
                <input
                  type="text"
                  id="returnAirwaybillNumber"
                  value={formData.returnAirwaybillNumber || ''}
                  onChange={(e) => onFieldChange('returnAirwaybillNumber', e.target.value)}
                  placeholder={t('ecomOrders.returnAirwaybillNumberPlaceholder')}
                  className={errors.returnAirwaybillNumber ? styles.error : ''}
                  readOnly={isReturnLocked}
                />
              </div>
              {errors.returnAirwaybillNumber && (
                <span className={styles.errorText}>{errors.returnAirwaybillNumber}</span>
              )}
            </div>

            {/* Items to Return */}
            {replacedOrderData && formData.returnItems?.length > 0 && (
              <div className={styles.formGroupFull}>
                <label>{t('ecomOrders.itemsToReturn')}</label>
                <div className={styles.returnItemsTableWrapper}>
                  <table className={styles.returnItemsTable}>
                    <thead>
                      <tr>
                        <th>{t('ecomOrders.item')}</th>
                        <th>{t('ecomOrders.remainingReturnableQty')}</th>
                        <th>{t('ecomOrders.returnQuantity')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.returnItems.map(item => (
                        <tr
                          key={item.replacedOrderItemId}
                          className={item.remainingQty <= 0 ? styles.disabledRow : ''}
                        >
                          <td>{item.productName}{item.variationSku ? ` — ${item.variationSku}` : ''}</td>
                          <td>{item.remainingQty}</td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              max={item.remainingQty}
                              step="1"
                              value={item.returnQty}
                              onChange={(e) => onUpdateReturnItemQty(item.replacedOrderItemId, e.target.value)}
                              disabled={item.remainingQty <= 0 || isReturnLocked}
                              readOnly={isReturnLocked}
                              className={styles.returnQtyInput}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {errors.returnItems && (
                  <span className={styles.errorText}>{errors.returnItems}</span>
                )}
              </div>
            )}

            {returnPackageOrder && (
              <div className={styles.formGroupFull}>
                <div className={styles.returnPackageNotice}>
                  <RotateCcw size={16} />
                  <span>{t('ecomOrders.returnPackageOrderCreated')}: {returnPackageOrder.orderNumber}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderStatusSection;